//
// # GraphicsCatalogueDialog
//
// Modal dialog for selecting a pre-defined graphics shape from the catalogue.
// Adds the selected shape to the active symbol at the viewport center.
//

import React, { useState, useCallback } from "react";
import { Modal } from "../Modal/Modal";
import { GraphicsCatalogue } from "EditorCore";
import type { GraphicsCatalogueEntry } from "EditorCore";

interface GraphicsCatalogueDialogProps {
  readonly onConfirm: (entry: GraphicsCatalogueEntry) => void;
  readonly onCancel: () => void;
}

export function GraphicsCatalogueDialog({
  onConfirm,
  onCancel,
}: GraphicsCatalogueDialogProps) {
  const [selected, setSelected] = useState(GraphicsCatalogue[0]?.name ?? "");

  const handleConfirm = useCallback(() => {
    const entry = GraphicsCatalogue.find((e) => e.name === selected);
    if (entry) onConfirm(entry);
  }, [selected, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Add from Graphics Catalog">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Shape selector */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Shape
          </label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {GraphicsCatalogue.map((entry) => (
              <option key={entry.name} value={entry.name}>
                {entry.name}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-ide-accent)",
              color: "#ffffff",
            }}
          >
            Add
          </button>
        </div>
      </div>
    </Modal>
  );
}
